import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../cn";

export interface EmptyStateProps {
  /** The glyph shown in the soft icon badge above the title. */
  readonly icon: LucideIcon;
  readonly title: string;
  readonly description?: ReactNode;
  /**
   * A control offering the obvious next step, typically a `Button` that opens
   * a create dialog. Optional: an empty state describing a read-only surface
   * (e.g. "no results for this filter") has nothing to act on.
   */
  readonly action?: ReactNode;
  readonly className?: string;
}

/**
 * The icon-badge-title-description shape used across admin-v2 wherever a list
 * or panel has nothing to show, generalized from the near-identical markup
 * that had accumulated independently in the documents tab, the inbox reading
 * pane and the incoming inbox queue.
 *
 * Deliberately unopinionated about its container: callers add their own
 * border/divider (e.g. `border-t border-border/60`) via `className`, since
 * some sit inside a card that already has one edge and some do not.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps): ReactNode {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-2 px-4 py-8 text-center",
        className,
      )}
    >
      <span className="grid size-10 place-items-center rounded-xl bg-muted text-muted-foreground">
        <Icon className="size-5" aria-hidden="true" />
      </span>
      <p className="text-sm font-medium">{title}</p>
      {description && (
        <p className="max-w-xs text-xs text-muted-foreground">{description}</p>
      )}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}
